/**
 * Wordings of «Учитель» v3 — family «plan» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * Two kinds of pools, one per theme family (THEME_FOCUS in ../index.ts says which move ideas serve which family):
 *  - `v3.why.<family>` — the second sentence of an advice: why this move serves the theme of this game;
 *  - `v3.themeTail.<family>` — a tail instead of the idea tail (blitz: one sentence), after ANY lead
 *    («Давай сходим {конём}», «{Конь} просится в бой», «По нашему плану идёт {конь}», «Ход {ферзём} хорош» …).
 *
 * Truth: a wording must hold for EVERY idea of its family (and its §6.3 pair condition), so the words speak of the
 * plan, not of one concrete move: center — pawn to the centre / pawn hits a centre pawn / support / a piece looks at
 * the centre (never «мы заняли центр»); gambit — never «мы отдали пешку» (only true a pawn down, §6.3); f7 — the moved
 * piece aims at the pawn by the king that is still at home; kingside / queenside — the target is on that side.
 * Tails carry no piece pronoun (the lead's piece may be feminine: «пешкой») and no «план» (the lead may say it).
 * True at the very first move too (the advice before move 1 may carry a theme link): nothing that assumes earlier
 * moves — no «дальше», «продолжаем / продолжается», «снова», «ещё (один / немного)», «уже», «всё ближе», «потихоньку»,
 * «ход за ходом … становится», «становится прочнее». Say what THIS move does for the plan or what the plan wants.
 * Stage words: «слабая пешка», «развитие», «гамбит», «контрудар» — from stage 2–3; «фланг», «открытая линия» — from 3;
 * at 1–2 it is «сторона короля / ферзя», «свободная дорога».
 * Length: a `why` sentence follows a whole advice, and at stages 1–2 the utterance is ≤ 16 words — short wordings are
 * the ones that survive; keep most of them ≤ 10 words. Avoid the ideas' own tails (v3.idea.*) and the announcements
 * (v3.theme.*, v3.goal.*, v3.takeaway.*): the same move may hear both.
 */
import type { LessonWordings } from '../types.ts';

const OLD = [3, 5] as const;
const FROM2 = [2, 5] as const;

export const WORDINGS: LessonWordings = {
  // ───────────────────────── center: pawn to the centre, a hit on a centre pawn, support, a piece looks at the centre
  'v3.why.center': [
    { t: 'Сегодня у нас одна забота — центр.' },
    { t: 'Из центра легко успеть и в атаку, и в защиту.' },
    { t: 'Центр — как вершина горки: оттуда всё видно.' },
    { t: 'Середина доски — главная площадь, и сегодня мы за неё спорим.' },
    { t: 'Всё по нашей теме: сегодня мы боремся за центр.' },
    { t: 'Чем крепче наш центр, тем свободнее нашим фигурам.' },
    { t: 'Вот эти четыре клетки в середине нам очень нужны.' },
    { t: 'В центре фигуры сильнее, чем на краю доски.' },
    { t: 'Центр — наша главная цель, к ней и шагаем.' },
    { t: 'Каждая пешка и фигура сегодня работает на центр.' },
    { t: 'Когда центр наш, фигурам соперника тесно.' },
    { t: 'Соперник тоже хочет центр — кто успеет первым?' },
  ],
  'v3.themeTail.center': [
    { t: '— так мы боремся за центр.' },
    { t: '— и центр понемногу станет нашим.' },
    { t: '— центр нужен нам, а не сопернику!' },
    { t: '— вот эти клетки мы и хотим держать.' },
    { t: '— тянемся к середине доски.' },
    { t: '— сегодня всё крутится вокруг центра.' },
    { t: '— к центру, к центру!' },
    { t: '— без центра сегодня никуда!' },
  ],

  // ───────────────────────── counterCenter: a hit on a centre pawn, own pawn to the centre, a trade in the centre
  'v3.why.counterCenter': [
    { t: 'Весь центр соперник не получит — мы с ним поспорим.' },
    { t: 'Наш план сегодня — мешать сопернику строить большой центр.' },
    { t: 'Мы в центре не зрители — мы тоже играем!' },
    { t: 'Делить центр с соперником мы готовы, а отдавать — нет.' },
    { t: 'Кто уступит центр, тому будет тесно, а мы не уступим.' },
    { t: 'Вот эти клетки в середине — за них сейчас весь спор.' },
    { t: 'Сегодня главное — спорить за каждую клетку в центре.' },
    { t: 'Контрудар по центру — наш план, и этот ход ему помогает.', stages: OLD },
    { t: 'Середина доски не должна достаться сопернику даром.' },
    { t: 'Соперник думает, что центр его, а мы с этим не согласны!' },
  ],
  'v3.themeTail.counterCenter': [
    { t: '— пусть соперник понервничает за центр.' },
    { t: '— нам в центре тоже найдётся место.' },
    { t: '— хозяйничать в центре сопернику не дадим.' },
    { t: '— так соперник не заберёт весь центр себе.' },
    { t: '— вот эти клетки сопернику не отдадим.' },
    { t: '— в споре за центр мы не молчим!' },
    { t: '— центр не только для соперника!' },
  ],

  // ───────────────────────── development: a piece out, a piece off the king's way, rooks connected, a look at the centre
  'v3.why.development': [
    { t: 'Спящая фигура не помогает ни в защите, ни в атаке.' },
    { t: 'Кто пока сидит дома, тот скоро тоже выйдет.' },
    { t: 'Чем больше наших фигур в игре, тем легче нам играть.' },
    { t: 'Сегодня мы соревнуемся, чьи фигуры проснутся быстрее.' },
    { t: 'Каждая фигура должна успеть выйти в бой — этим мы и заняты.' },
    { t: 'Развитие — это когда фигуры выходят в игру: сегодня это главное.', stages: FROM2 },
    { t: 'Фигуры, которые сидят дома, мешают и королю, и ладьям.' },
    { t: 'Фигура дома — как игрок на скамейке, а нам нужна вся команда.' },
    { t: 'Атаковать можно только проснувшимися фигурами, поэтому будим всех.', stages: OLD },
    { t: 'Каждая фигура в игре — новый помощник.' },
    { t: 'Когда все фигуры в игре, начинается самое интересное.' },
  ],
  'v3.themeTail.development': [
    { t: '— будим фигуры по очереди.' },
    { t: '— все фигуры в игру, король в домик!' },
    { t: '— пусть выходят все!' },
    { t: '— команда собирается!' },
    { t: '— каждой фигуре найдём работу.' },
    { t: '— развитие сегодня важнее всего.', stages: FROM2 },
    { t: '— никто не должен скучать дома.' },
    { t: '— готовим армию к бою.' },
  ],

  // ───────────────────────── castle: the castling itself or a piece off the king's way (true for both)
  'v3.why.castle': [
    { t: 'Сильные игроки прячут короля пораньше — и мы так делаем.' },
    { t: 'Король — самая важная фигура, его надо беречь.' },
    { t: 'Когда король спрятан, всем нашим фигурам спокойнее.' },
    { t: 'Наша первая забота сегодня — спрятать короля.' },
    { t: 'Рокировка — наша цель, и каждый шаг к ней важен.' },
    { t: 'Домик строят заранее, пока соперник не напал.' },
    { t: 'Король без домика — как без шапки в мороз.' },
    { t: 'Откроются линии — и король в центре окажется под ударом, поэтому спешим.', stages: OLD },
    { t: 'Спрячем короля — заодно и ладья проснётся.' },
    { t: 'Рокировка — единственный ход сразу двумя фигурами, и он нам очень нужен.' },
  ],
  'v3.themeTail.castle': [
    { t: '— пора королю прятаться.' },
    { t: '— королю не место посреди доски.' },
    { t: '— король любит свой домик.' },
    { t: '— так король скорее окажется в домике.' },
    { t: '— бережём короля заранее.' },
    { t: '— король не должен гулять в центре.' },
    { t: '— в домике королю будет спокойнее.' },
  ],

  // ───────────────────────── f7: the moved piece aims at the pawn by the king (pawn in place, king at home)
  'v3.why.f7': [
    { t: 'Наша мишень сегодня — пешка рядом с королём соперника.' },
    { t: 'Этот ход приближает нас к слабой пешке у короля.', stages: FROM2 },
    { t: 'Сегодня мы целимся вот в эту пешку у короля соперника.' },
    { t: 'Когда на пешку у короля смотрят наши фигуры, сопернику тревожно.' },
    { t: 'Пока король соперника не спрятался, его соседке-пешке неспокойно.' },
    { t: 'Слабое место соперника — пешка у короля, туда и направляем фигуры.', stages: FROM2 },
    { t: 'Эту пешку у короля защищать трудно, вот мы на неё и нацелились.' },
    { t: 'Сегодня мы охотимся за пешкой, которая прикрывает короля соперника.' },
    { t: 'Удар по слабой пешке у короля — старый приём, и мы его готовим.', stages: OLD },
    { t: 'Пешка-соседка короля — как хлипкая дверь, и мы в неё стучимся.' },
    { t: 'Наши фигуры сегодня ищут дорожку к пешке у короля соперника.' },
    { t: 'Мы подбираемся к пешке у короля, как кот к мышке.' },
    { t: 'Выводим фигуры так, чтобы они смотрели на пешку у короля.' },
    { t: 'К пешке у короля соперника идут гости — наши фигуры.' },
  ],
  'v3.themeTail.f7': [
    { t: '— целимся в пешку у короля!' },
    { t: '— держим на мушке слабую пешку у короля.', stages: FROM2 },
    { t: '— тихо-тихо крадёмся к пешке у короля.' },
    { t: '— тук-тук, пешка у короля!' },
    { t: '— и соседке короля станет неуютно.' },
    { t: '— охотимся на пешку у короля.' },
    { t: '— у соперника есть слабое место, и мы его нашли.', stages: FROM2 },
    { t: '— пешку рядом с королём мы не оставим в покое.' },
    { t: '— глядим прямо на пешку у короля.' },
    { t: '— пусть соперник поволнуется за свою пешку.' },
  ],

  // ───────────────────────── fortress: support of a centre pawn, a defence, a pawn to the centre
  'v3.why.fortress': [
    { t: 'Сегодня мы строим, а атака подождёт.' },
    { t: 'Прочная крепость не боится нападений, вот мы её и строим.' },
    { t: 'Даром мы ничего не отдаём, а центр держим пешками.' },
    { t: 'В крепости каждая пешка и фигура защищает соседа.' },
    { t: 'Стены нашей крепости — пешки в центре, за ними спокойно выводить фигуры.' },
    { t: 'Вот эти клетки в центре — сердце нашей крепости.' },
    { t: 'Не спешим: крепость строят камень за камнем.' },
    { t: 'О хорошую крепость соперник обломает зубы.' },
    { t: 'Прочный центр из пешек — основа нашего плана на всю партию.', stages: OLD },
    { t: 'Когда всё защищено, соперник не знает, куда напасть.' },
  ],
  'v3.themeTail.fortress': [
    { t: '— так крепость будет прочнее.' },
    { t: '— из таких ходов и строится крепость.' },
    { t: '— кладём камень в нашу крепость.' },
    { t: '— надёжно, как в крепости.' },
    { t: '— спешить нам некуда.' },
    { t: '— вот эти клетки под охраной нашей крепости.' },
    { t: '— соперник упрётся в нашу стену.' },
  ],

  // ───────────────────────── gambit: a hit on a centre pawn, a road opened, a piece out (never «мы отдали пешку»)
  'v3.why.gambit': [
    { t: 'В гамбите всё решает скорость, поэтому мы не теряем ни хода.', stages: OLD },
    { t: 'Сегодня фигуры в игре и центр нам дороже одной пешки.', stages: OLD },
    { t: 'Гамбит — это гонка: чьи фигуры выйдут раньше, у того и игра.', stages: OLD },
    { t: 'Ради вот этих клеток в центре мы сегодня и торопимся.' },
    { t: 'Мы торопимся, как в эстафете: фигура за фигурой — в бой!' },
    { t: 'Гамбит — игра для смелых: пешку считаем потом.', stages: OLD },
    { t: 'В гамбите пешка — это цена за быстрое развитие и сильный центр.', stages: OLD },
    { t: 'Каждый ход сегодня на счету, и этот работает на наш план.' },
    { t: 'Сегодня мы играем быстро: будим фигуры и спорим за центр.' },
    { t: 'Нам важна каждая фигура в игре и каждая клетка в центре.' },
    { t: 'Кто медлит, тот отстаёт, а мы отставать не хотим.' },
    { t: 'Сегодня мы как гонщики: быстрее выводим фигуры, быстрее берём центр.' },
    { t: 'Ради центра нам и пешки не жалко.' },
    { t: 'Главное сегодня — скорость и центр.' },
    { t: 'Наша задача — вывести фигуры и занять центр быстрее соперника.' },
  ],
  'v3.themeTail.gambit': [
    { t: '— спешим к центру!' },
    { t: '— скорость сейчас важнее пешки.', stages: OLD },
    { t: '— фигуры торопятся в бой!' },
    { t: '— в гамбите медлить нельзя.', stages: OLD },
    { t: '— кто быстрее, тот и в центре!' },
    { t: '— вот эти клетки нам и нужны.' },
    { t: '— ни одного хода впустую.' },
    { t: '— ради центра стоит поспешить.' },
    { t: '— быстро, но с умом!' },
    { t: '— центр сам себя не займёт!' },
  ],

  // ───────────────────────── openFile: a rook to an open / half-open file, a rook to the 7th, rooks connected
  'v3.why.openFile': [
    { t: 'Ладьи любят свободные дороги, и сегодня мы их ищем.' },
    { t: 'Ладья в углу скучает, а на свободной дороге она стреляет далеко.' },
    { t: 'Открытые линии — главные дороги для ладей, и мы за них боремся.', stages: OLD },
    { t: 'Пусть ничто не мешает нашим ладьям — так мы задумали.' },
    { t: 'Ладьи бьют далеко, почти как ферзь, — им нужна открытая линия.', stages: OLD },
    { t: 'Две ладьи рядом — большая сила, и мы к этому ведём.' },
    { t: 'Вот эта линия нам и нужна: по ней ладья пойдёт далеко.' },
    { t: 'Ладьи выходят в бой последними, зато бьют издалека.' },
    { t: 'Ладья не прыгает, как конь, — ей нужна дорога без своих пешек.' },
    { t: 'Для наших ладей сегодня найдётся большая работа.' },
    { t: 'Пешки перед ладьёй — как забор, а ей нужен простор.' },
    { t: 'Сильная ладья — та, у которой длинная дорога впереди.' },
  ],
  'v3.themeTail.openFile': [
    { t: '— ладьям скучать не придётся.' },
    { t: '— так ладьи выйдут на простор.' },
    { t: '— открытые линии ждут наших ладей.', stages: OLD },
    { t: '— ладьи готовятся к делу.' },
    { t: '— вот эта линия пригодится ладье.' },
    { t: '— ладьям пора на охоту.' },
    { t: '— дорогу ладьям!' },
    { t: '— и ладьи заиграют в полную силу.' },
    { t: '— ладье есть где разогнаться!' },
  ],

  // ───────────────────────── kingsideAttack: attack / check / mate threat, the target near the king on the king's side
  'v3.why.kingsideAttack': [
    { t: 'Наш план — атака на короля соперника, и мы к нему подбираемся.' },
    { t: 'Мы стучимся в домик к королю соперника.' },
    { t: 'Чем ближе наши фигуры к королю соперника, тем ему неуютнее.' },
    { t: 'Фигуры собираются у чужого короля, как тучи перед грозой.' },
    { t: 'На королевском фланге сегодня главная работа для наших фигур.', stages: OLD },
    { t: 'Сегодня мы играем на той стороне, где стоит король соперника.' },
    { t: 'Наша цель — чтобы королю соперника некуда было спрятаться.' },
    { t: 'Каждая наша фигура у короля соперника — незваный гость.' },
    { t: 'Короля соперника мы не оставим в покое.' },
    { t: 'Королевский фланг — наше поле боя, и мы туда подтягиваем фигуры.', stages: OLD },
    { t: 'Сильная атака — это когда фигуры приходят все вместе.' },
    { t: 'Короля соперника мы берём в кольцо.' },
  ],
  'v3.themeTail.kingsideAttack': [
    { t: '— готовим королю соперника сюрприз!' },
    { t: '— гроза собирается у короля соперника.' },
    { t: '— королю соперника пора волноваться!' },
    { t: '— давим на королевском фланге.', stages: OLD },
    { t: '— все к королю соперника!' },
    { t: '— наши фигуры тянутся к королю соперника.' },
    { t: '— идём в гости к королю соперника!' },
    { t: '— ближе, ближе к королю соперника!' },
    { t: '— соберём силы у короля соперника.' },
  ],

  // ───────────────────────── queensideAttack: an attack or a rook file on the queen's side
  'v3.why.queensideAttack': [
    { t: 'Сегодня мы играем на стороне ферзя — там наша главная работа.' },
    { t: 'На ферзевом фланге у нас свой план, и мы его выполняем.', stages: OLD },
    { t: 'Наши силы сегодня собираются на стороне ферзя.' },
    { t: 'Мы хотим, чтобы на стороне ферзя командовали наши фигуры.' },
    { t: 'Сторона ферзя — наша сторона сегодня, и мы туда наступаем.' },
    { t: 'На стороне ферзя тоже можно атаковать — вот и атакуем.' },
    { t: 'Играем на ферзевом фланге: там ладьям и пешкам есть где развернуться.', stages: OLD },
    { t: 'Этот ход — шаг к нашей цели на стороне ферзя.' },
    { t: 'Далеко от центра тоже бывает интересно: сегодня играем на стороне ферзя.' },
    { t: 'Сегодня мы идём в поход на сторону ферзя.' },
    { t: 'Хозяевами на стороне ферзя должны стать мы.' },
    { t: 'Мы ищем слабые места соперника на стороне ферзя.' },
  ],
  'v3.themeTail.queensideAttack': [
    { t: '— работаем на стороне ферзя.' },
    { t: '— вперёд, на сторону ферзя!' },
    { t: '— ферзевый фланг будет нашим.', stages: OLD },
    { t: '— наступаем на стороне ферзя.' },
    { t: '— пусть соперник следит за стороной ферзя!' },
    { t: '— игра уходит на сторону ферзя.' },
    { t: '— на стороне ферзя начинается интересное!' },
    { t: '— на стороне ферзя нам есть чем заняться!' },
    { t: '— забираем сторону ферзя себе.' },
  ],
};
